import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Heading,
  Divider,
  VStack,
  SimpleGrid,
  Flex,
  Button,
  useToast,
  ButtonGroup,
  Switch,
  FormLabel,
} from '@chakra-ui/react';

import axios from 'axios';
import { parseISO, startOfDay } from 'date-fns';

import { endOfDay } from 'date-fns/esm';
import { MaskedInput } from '../../../../../components/Form/MaskedInput';
import { DefaultLayout } from '../../../_layout/DefaultLayout';
import { DeleteConfirmationModal } from '../../../../../components/DeleteConfirmationModal';
import { showAdvertisementsService } from '../../../../../services/Advertisements/ShowAdvertisementsService';
import { updateAdvertisementsService } from '../../../../../services/Advertisements/UpdateAdvertisementsService';
import { AvatarDropzone } from '../../../../../components/Form/AvatarDropzone';
import { deleteAdvertisementAvatarsService } from '../../../../../services/Advertisements/DeleteAdvertisementAvatarsService';
import { updateAdvertisementAvatarsService } from '../../../../../services/Advertisements/UpdateAdvertisementAvatarsService';
import { DatePicker } from '../../../../../components/Form/DatePicker';
import {
  AsyncSelect,
  SelectOption,
} from '../../../../../components/Form/AsyncSelect';
import { listAdvertisementCategoriesService } from '../../../../../services/AdvertisementCategories/ListAdvertisementCategoriesService';
import { listSellersService } from '../../../../../services/Sellers/ListSellersService';
import {
  maskMoney,
  unmaskNumber,
} from '../../../../../utils/formatters/handleMask';
import { useAuth } from '../../../../../hooks/auth';
import { translateError } from '../../../../../utils/errors';
import { TimeInputWithScale } from '../../../../../components/Form/TimeInputWithScale';

type UpdateAdvertisementFormData = {
  couponsAmount?: number;
  couponsMaxAgeInMinutes?: number;
  couponsMaxUsagePerUser?: number;
  description?: string;
  discount?: string | number | null;
  expirationDate?: Date | null;
  title: string;
  selectedCategory: SelectOption;
  selectedSeller: SelectOption;
};

interface ILocationState {
  advertisementId: string;
}

const advertisementUpdateFormSchema = Yup.object().shape({
  couponsAmount: Yup.number()
    .integer('Apenas valores inteiros.')
    .positive('Apenas valores maiores que zero.')
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .when('couponsMaxUsagePerUser', (couponsMaxUsagePerUser, couponsAmount) =>
      couponsMaxUsagePerUser
        ? couponsAmount.min(
            Yup.ref('couponsMaxUsagePerUser'),
            'Quantidade total deve ser maior que o máximo por usuário',
          )
        : couponsAmount,
    ),
  couponsMaxAgeInMinutes: Yup.number()
    .integer('Apenas valores inteiros.')
    .positive('Apenas valores maiores que zero.')
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  couponsMaxUsagePerUser: Yup.number()
    .integer('Apenas valores inteiros.')
    .positive('Apenas valores maiores que zero.')
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  description: Yup.string()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  discount: Yup.number()
    .integer('Apenas valores inteiros.')
    .positive('Apenas valores maiores que zero.')
    .nullable()
    .transform((_, originalValue) =>
      originalValue === '' ? null : unmaskNumber(originalValue),
    ),
  expirationDate: Yup.date()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .min(startOfDay(new Date()), 'Data inválida, mínimo hoje'),
  title: Yup.string().required('Título requerido'),
  selectedCategory: Yup.object()
    .shape({
      label: Yup.string(),
      value: Yup.string().uuid(),
    })
    .required('Categoria requerida'),
  selectedSeller: Yup.object()
    .shape({
      label: Yup.string().required('Loja requerida'),
      value: Yup.string().uuid().required('Loja requerida'),
    })
    .required('Loja requerida'),
});

export const AdvertisementUpdate = (): JSX.Element => {
  const { goBack } = useHistory();
  const toast = useToast();
  const { user } = useAuth();

  const { state } = useLocation<ILocationState>();

  const [updatingAdvertisement, setUpdatingAdvertisement] =
    useState<UpdateAdvertisementFormData>();
  const [avatar, setAvatar] = useState<File>();
  const [avatarUrl, setAvatarUrl] = useState<string>();
  const [
    isDeleteConfirmationModalVisible,
    setIsDeleteConfirmationModalVisible,
  ] = useState(false);
  const [isActive, setIsActive] = useState(false);

  const { register, handleSubmit, formState, reset, control } = useForm({
    resolver: yupResolver(advertisementUpdateFormSchema),
  });

  const { errors } = formState;
  const { advertisementId } = state;

  const handleLoadCategorySelectOption = useCallback(
    async (name?: string): Promise<SelectOption[]> => {
      const { items: sellers } = await listAdvertisementCategoriesService({
        name,
        limit: 4,
      });

      const parsedCategoriesSelectOption: SelectOption[] = [
        ...sellers.map((seller) => ({
          label: seller.name,
          value: seller.id,
        })),
      ];

      return parsedCategoriesSelectOption;
    },
    [],
  );

  const handleLoadSellerSelectOption = useCallback(
    async (name?: string): Promise<SelectOption[]> => {
      const { items: sellers } = await listSellersService({ name, limit: 4 });

      const parsedSellersSelectOption: SelectOption[] = [
        ...sellers.map((seller) => ({
          label: seller.name,
          value: seller.id,
        })),
      ];

      return parsedSellersSelectOption;
    },
    [],
  );

  useEffect(() => {
    async function loadAdvertisement(): Promise<void> {
      try {
        const advertisementData = await showAdvertisementsService(
          advertisementId,
        );
        setAvatarUrl(advertisementData.avatarUrl || undefined);

        setIsActive(advertisementData.isActive);

        setUpdatingAdvertisement({
          couponsAmount: advertisementData.couponsAmount,
          couponsMaxAgeInMinutes: advertisementData.couponsMaxAgeInMinutes,
          couponsMaxUsagePerUser: advertisementData.couponsMaxUsagePerUser,
          description: advertisementData.description,
          discount: advertisementData.discount
            ? maskMoney(advertisementData.discount)
            : undefined,
          expirationDate: advertisementData.expirationDate
            ? parseISO(advertisementData.expirationDate)
            : undefined,
          title: advertisementData.title,
          selectedCategory: {
            label: advertisementData.category.name,
            value: advertisementData.category.id,
          },
          selectedSeller: {
            label: advertisementData.seller.name,
            value: advertisementData.seller.id,
          },
        });
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao carregar dados',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao carregar os dados do anúncio, tente novamente',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    }

    loadAdvertisement();
  }, [advertisementId, reset, toast]);

  useEffect(() => {
    reset(updatingAdvertisement);
  }, [reset, updatingAdvertisement]);

  const handleChangeAvatar = useCallback((file: File) => {
    setAvatar(file);
    setAvatarUrl(URL.createObjectURL(file));
  }, []);

  const handleToggleIsActive = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setIsActive(event.target.checked);
    },
    [],
  );

  const handleToggleDeleteConfirmationModal = useCallback(() => {
    setIsDeleteConfirmationModalVisible((prevState) => !prevState);
  }, []);

  const handleDeleteAvatar = useCallback(async () => {
    await deleteAdvertisementAvatarsService(advertisementId);

    setAvatar(undefined);
    setAvatarUrl(undefined);
    handleToggleDeleteConfirmationModal();
  }, [advertisementId, handleToggleDeleteConfirmationModal]);

  const handleUpdateAdvertisement: SubmitHandler<UpdateAdvertisementFormData> =
    useCallback(
      async ({
        couponsAmount,
        couponsMaxAgeInMinutes,
        couponsMaxUsagePerUser,
        description,
        discount,
        expirationDate,
        title,
        selectedCategory,
        selectedSeller,
      }) => {
        try {
          await updateAdvertisementsService({
            advertisementId,
            categoryId: selectedCategory.value as string,
            couponsAmount,
            couponsMaxAgeInMinutes,
            couponsMaxUsagePerUser,
            description,
            discount: discount ? Number(discount) : null,
            expirationDate: expirationDate
              ? endOfDay(expirationDate)
              : expirationDate,
            isActive,
            title,
            sellerId: selectedSeller.value as string,
          });

          if (avatar) {
            const formData = new FormData();

            formData.append('avatar', avatar);

            await updateAdvertisementAvatarsService({
              advertisementId,
              avatarData: formData,
            });
          }

          toast({
            title: 'Editado com sucesso',
            description: 'O anúncio foi editado corretamente',
            status: 'success',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });

          goBack();
        } catch (err) {
          if (axios.isAxiosError(err) && err.response?.status !== 401) {
            toast({
              title: 'Falha ao editar',
              description:
                translateError({ message: err.response?.data.message }) ||
                'Ocorreu um erro ao editar o anúncio, tente novamente',
              status: 'error',
              duration: 3000,
              isClosable: true,
              variant: 'subtle',
              position: 'top-right',
            });
          }
        }
      },
      [advertisementId, isActive, avatar, toast, goBack],
    );

  return (
    <DefaultLayout>
      <DeleteConfirmationModal
        isOpen={isDeleteConfirmationModalVisible}
        onClose={handleToggleDeleteConfirmationModal}
        onConfirm={handleDeleteAvatar}
      />

      <Box
        as="form"
        flex="1"
        borderRadius={8}
        bg="white"
        p="8"
        onSubmit={handleSubmit(handleUpdateAdvertisement)}
      >
        <Heading size="lg" fontWeight="normal">
          Editar anúncio
        </Heading>

        <Divider my="6" borderColor="gray.300" />

        <Flex justify="center" mb="8">
          <AvatarDropzone
            avatarUrl={avatarUrl}
            onChange={handleChangeAvatar}
            onDelete={handleToggleDeleteConfirmationModal}
          />
        </Flex>

        <VStack spacing="8">
          <MaskedInput
            label="Título"
            error={errors.title}
            {...register('title')}
          />

          <SimpleGrid minChildWidth="240px" spacing="8" w="100%">
            <MaskedInput
              label="Desconto"
              mask="money"
              error={errors.discount}
              {...register('discount')}
            />

            <DatePicker
              label="Data de encerramento"
              minDate={new Date()}
              control={control}
              error={errors.expirationDate}
              {...register('expirationDate')}
            />
          </SimpleGrid>

          <SimpleGrid minChildWidth="240px" spacing="8" w="100%">
            <AsyncSelect
              label="Loja"
              name="selectedSeller"
              loadOptions={handleLoadSellerSelectOption}
              isDisabled={user.role !== 'ADMIN'}
              control={control}
              error={errors.selectedSeller}
            />

            <AsyncSelect
              label="Categoria"
              name="selectedCategory"
              loadOptions={handleLoadCategorySelectOption}
              control={control}
              error={errors.selectedCategory}
            />
          </SimpleGrid>

          <SimpleGrid minChildWidth="240px" spacing="8" w="100%">
            <VStack spacing="8">
              <MaskedInput
                label="Quantidade de cupons"
                error={errors.couponsAmount}
                {...register('couponsAmount')}
              />

              <MaskedInput
                label="Máx. cupons por usuário"
                error={errors.couponsMaxUsagePerUser}
                {...register('couponsMaxUsagePerUser')}
              />

              <TimeInputWithScale
                label="Validade dos cupons"
                name="couponsMaxAgeInMinutes"
                control={control}
                error={errors.couponsMaxAgeInMinutes}
              />
            </VStack>

            <VStack spacing="8">
              <MaskedInput
                label="Descrição"
                as="textarea"
                minHeight="160px"
                resize="none"
                py="2"
                error={errors.description}
                {...register('description')}
              />

              <Flex flex={1} w="100%" alignItems="flex-end">
                <Box mx="auto">
                  <FormLabel htmlFor="isActive">Ativo</FormLabel>
                  <Switch
                    name="isActive"
                    id="isActive"
                    isChecked={isActive}
                    onChange={handleToggleIsActive}
                  />
                </Box>
              </Flex>
            </VStack>
          </SimpleGrid>
        </VStack>

        <Flex mt="12" justify="flex-end">
          <ButtonGroup>
            <Button colorScheme="blackAlpha" onClick={goBack}>
              Cancelar
            </Button>
            <Button
              type="submit"
              colorScheme="green"
              isLoading={formState.isSubmitting}
            >
              Salvar
            </Button>
          </ButtonGroup>
        </Flex>
      </Box>
    </DefaultLayout>
  );
};
